import { BookingState, WorkTaskDTO2 } from '../api/api';

export type WorkPermitTimeSource = 'bookings' | 'plannedArrival' | 'taskDates';

export interface WorkPermitTimes {
  start: string;
  end?: string;
  source: WorkPermitTimeSource;
}

const toTime = (date: string) => new Date(date).getTime();

/**
 * The time a work permit should cover: the start of the first and the end of the last booking on the
 * main task (absence and canceled bookings left out). Without bookings it is the planned arrival plus
 * the planned duration, and without those the earliest start date and deadline of the main task.
 */
export const getWorkPermitTimes = (mainTask: WorkTaskDTO2): WorkPermitTimes | undefined => {
  const bookings = (mainTask.bookings ?? []).filter(
    (booking) => !booking.isAbsenceBooking && booking.bookingState !== BookingState.Canceled && booking.appointmentFrom
  );

  if (bookings.length > 0) {
    const starts = bookings.map((booking) => booking.appointmentFrom as string);
    const ends = bookings.map((booking) => booking.appointmentTo).filter((end): end is string => !!end);
    return {
      start: starts.reduce((first, start) => (toTime(start) < toTime(first) ? start : first)),
      end: ends.length > 0 ? ends.reduce((last, end) => (toTime(end) > toTime(last) ? end : last)) : undefined,
      source: 'bookings'
    };
  }

  if (mainTask.plannedArrival) {
    const end =
      mainTask.plannedDuration !== undefined
        ? new Date(toTime(mainTask.plannedArrival) + mainTask.plannedDuration * 60000).toISOString()
        : undefined;
    return { start: mainTask.plannedArrival, end, source: 'plannedArrival' };
  }

  if (mainTask.earliestStartDate) {
    return { start: mainTask.earliestStartDate, end: mainTask.deadline, source: 'taskDates' };
  }

  return undefined;
};

export const describeWorkPermitTimeSource = (source: WorkPermitTimeSource): string => {
  switch (source) {
    case 'bookings':
      return 'Tiderne er hentet fra hovedopgavens bookinger: start på første booking og slut på sidste booking.';
    case 'plannedArrival':
      return 'Hovedopgaven har ingen bookinger. Tiderne er beregnet ud fra planlagt ankomst og planlagt varighed.';
    case 'taskDates':
      return 'Hovedopgaven har hverken bookinger eller planlagt ankomst. Tiderne er hentet fra hovedopgavens startdato og deadline.';
  }
};

const isSameMinute = (first: string, second: string) => Math.floor(toTime(first) / 60000) === Math.floor(toTime(second) / 60000);

/**
 * Whether the time of a work permit differs from the time of its main task. A missing time on the
 * main task, or on the work permit when the main task has one, counts as a mismatch only for the
 * parts the main task actually has.
 */
export const hasWorkPermitTimeMismatch = (workPermit: WorkTaskDTO2, mainTask: WorkTaskDTO2): boolean => {
  const expected = getWorkPermitTimes(mainTask);
  if (!expected) return false;

  const startMatches = !!workPermit.earliestStartDate && isSameMinute(workPermit.earliestStartDate, expected.start);
  const endMatches = !expected.end || (!!workPermit.deadline && isSameMinute(workPermit.deadline, expected.end));
  return !startMatches || !endMatches;
};
